import { useCallback, useEffect, useId, useState } from 'react';
import type * as React from 'react';
import { Page, Notice } from '@/components/page';
import { ErrorState } from '@/components/states';
import { Button } from '@/components/ui/button';
import { FormField, Input } from '@/components/ui/field';
import { rawRequest } from '@/api/client';
import { ApiError } from '@/api/errors';
import { notify } from '@/lib/notify';

interface SupportContact {
  supportEmail: string;
}

/**
 * The address the app's help and support screen shows.
 *
 * ⚠️ Not a Refine resource. Refine's data provider is built around a collection
 * with ids — list, create, update one row of many — and this is a single value
 * behind a fixed path. Modelling it as a resource would mean inventing an id
 * for a row the API never exposes one for.
 *
 * Editing this redirects where every user's support request goes, so the field
 * starts populated with the live value rather than blank: an empty box invites
 * someone to save a half-typed address over a working one.
 */
export function SupportSettingsPage(): React.JSX.Element {
  const emailId = useId();

  const [email, setEmail] = useState('');
  const [saved, setSaved] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<Error | null>(null);
  const [fieldError, setFieldError] = useState<string | undefined>();
  const [pending, setPending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);

    try {
      const response = await rawRequest('/admin/settings/support');
      const body = (await response.json()) as SupportContact;

      setEmail(body.supportEmail);
      setSaved(body.supportEmail);
    } catch (err) {
      setLoadError(err instanceof Error ? err : new Error('Could not load the support settings'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setPending(true);
    setFieldError(undefined);

    try {
      const response = await rawRequest('/admin/settings/support', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ supportEmail: email.trim() }),
      });
      const body = (await response.json()) as SupportContact;

      setEmail(body.supportEmail);
      setSaved(body.supportEmail);
      notify.success('Saved', body.supportEmail);
    } catch (err) {
      // The API validates the address; surface its message rather than
      // second-guessing it in the browser.
      if (err instanceof ApiError) setFieldError(err.fieldErrors['supportEmail'] ?? err.message);
      notify.error(err, 'The support address was not saved');
    } finally {
      setPending(false);
    }
  };

  if (loadError) return <ErrorState error={loadError} onRetry={() => void load()} />;

  return (
    <Page
      title="Help and support"
      description="The contact address the mobile app shows on its help screen."
    >
      <Notice title="Live for every user">
        Saving takes effect immediately — every app picks the new address up on its next request,
        with no release needed.
      </Notice>

      <form
        onSubmit={(event) => {
          void submit(event);
        }}
        noValidate
        className="mt-6 max-w-lg space-y-4"
      >
        <FormField
          label="Support email"
          htmlFor={emailId}
          hint="Shown to every user. Make sure someone is reading it."
          error={fieldError}
          required
        >
          <Input
            id={emailId}
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            maxLength={254}
            disabled={loading}
            required
          />
        </FormField>

        <Button
          type="submit"
          variant="primary"
          loading={pending}
          // Nothing to save when it is unchanged, and an empty box must never
          // overwrite a working address.
          disabled={loading || !email.trim() || email.trim() === saved}
        >
          Save
        </Button>
      </form>
    </Page>
  );
}
